import { Slot } from "@radix-ui/react-slot";
import type { LucideIcon } from "lucide-react";
import { type ButtonHTMLAttributes, type MouseEvent, type ReactNode, useState } from "react";
import { Icon } from "../icons/Icon";
import { cx, DISABLED, FOCUS_RING, TOUCH_TARGET } from "../styles";

/**
 * The button.
 *
 * Four variants and three sizes, which is the whole vocabulary: a screen that
 * needs a fifth has a design problem rather than a component problem. The
 * design draws them at 34px, and they grow to a 48px minimum below 640px
 * because a thumb is not a mouse.
 *
 * Two things here are less obvious than they look.
 *
 * **Loading keeps the width.** The spinner replaces the icon slot rather than
 * the label, so pressing Save does not make the button shrink and the row
 * reflow under the cursor — which on a slow connection means the next control
 * jumps into the place the pointer is already moving towards. It is also
 * `aria-busy` and genuinely non-interactive, because a button that looks busy
 * and still fires is how a double-submit happens.
 *
 * **A press that returns a promise is pending until it settles (ADJ-M03-1).**
 * `onClick={() => save.mutateAsync(values)}` shows the spinner and refuses a
 * second press on its own, so a slow request never looks like a frozen
 * button and nobody has to thread a `loading` flag for it. The button only
 * watches the promise: handling a rejection stays the caller's job, inside
 * its own function.
 *
 * **`asChild` renders the caller's element.** A navigation control is a link,
 * not a button: it has to be middle-clickable, copyable and announced as a
 * link. `<Button asChild><Link to="…">…</Link></Button>` keeps the styling and
 * the semantics of what it actually is.
 */

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "lg";

const VARIANTS: Record<ButtonVariant, string> = {
	primary:
		"bg-brand text-fg-on-brand border-brand hover:bg-brand-hover hover:border-brand-hover font-semibold shadow-xs",
	secondary:
		"bg-bg-surface text-fg border-border-strong hover:bg-bg-subtle hover:border-fg-subtle font-semibold shadow-xs",
	// No border and no fill: a ghost is for the third action in a row, where a
	// third outline would make the row read as three equal choices. In the link
	// ink rather than muted grey (ADJ-M01-1): with no hover on a phone, a grey
	// label with no outline read as plain text, not as something to tap.
	ghost: "bg-transparent text-brand-ink border-transparent hover:bg-bg-subtle font-semibold",
	// `--danger-fg` as the fill and `--danger-bg` as the text, which is the
	// inverse of the chip. A destructive action should not be the same weight as
	// a status label.
	danger:
		"bg-danger-fg text-danger-bg border-danger-fg hover:bg-danger-hover hover:border-danger-hover font-semibold shadow-xs",
};

// Heights match the fields beside them: a 36px button next to a 36px input
// (ADJ-M03-1; it was 34 against 36).
const SIZES: Record<ButtonSize, string> = {
	sm: "h-7 text-xs gap-1.5",
	md: "h-9 text-[13px] gap-2",
	lg: "h-[42px] text-sm gap-2",
};

/**
 * The inline padding, kept apart from the height so a square button has none.
 * Two padding utilities on one element are won by stylesheet order, not class
 * order — which is how an icon button's `px-0` lost to `px-3` and squeezed
 * its glyph to a 2px dot: the "three dots" that showed as one (ADJ-M03-1).
 */
const PADDING: Record<ButtonSize, string> = { sm: "px-3", md: "px-3.5", lg: "px-4.5" };
const SQUARE: Record<ButtonSize, string> = {
	sm: "w-7 max-sm:w-12",
	md: "w-9 max-sm:w-12",
	lg: "w-[42px] max-sm:w-12",
};

const BASE =
	"inline-flex items-center justify-center rounded-md border cursor-pointer " +
	"whitespace-nowrap select-none transition-[background-color,border-color,color,box-shadow,scale] " +
	"duration-150 ease-[var(--ease-out)] enabled:active:scale-[0.97] motion-reduce:transition-none " +
	"motion-reduce:enabled:active:scale-100";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
	variant?: ButtonVariant;
	size?: ButtonSize;
	/** Shows a spinner, marks the control busy, and refuses presses. */
	loading?: boolean;
	/** A leading icon. Hidden from assistive technology; the label names the action. */
	icon?: LucideIcon;
	/** A trailing icon, for a control that opens something. */
	iconEnd?: LucideIcon;
	/** Mirror the icons in Arabic. For chevrons and arrows, never for objects. */
	mirrorIcons?: boolean;
	/** Render the child element instead of a `<button>`, keeping these styles. */
	asChild?: boolean;
	/** A square with no inline padding. For {@link IconButton}. */
	square?: boolean;
	children?: ReactNode;
}

export function Button({
	variant = "secondary",
	size = "md",
	loading = false,
	icon,
	iconEnd,
	mirrorIcons = false,
	asChild = false,
	square = false,
	disabled,
	className,
	children,
	type,
	onClick,
	...rest
}: ButtonProps) {
	const [pending, setPending] = useState(false);
	const busy = loading || pending;
	const Component = asChild ? Slot : "button";
	const iconSize = size === "sm" ? 14 : 16;

	const classes = cx(
		BASE,
		VARIANTS[variant],
		SIZES[size],
		square ? SQUARE[size] : PADDING[size],
		FOCUS_RING,
		TOUCH_TARGET,
		DISABLED,
		busy && "cursor-wait",
		className,
	);

	// `asChild` hands everything to the caller's element: a link has no `type`,
	// no `disabled` and no busy state, and forwarding them would put invalid
	// attributes on an anchor.
	if (asChild) {
		return (
			<Component className={classes} onClick={onClick} {...rest}>
				{children}
			</Component>
		);
	}

	return (
		<button
			// Explicit, because the HTML default is `submit` and a button inside a
			// form that was only meant to open a drawer will otherwise submit it.
			type={type ?? "button"}
			className={classes}
			disabled={disabled === true || busy}
			aria-busy={busy || undefined}
			onClick={
				onClick === undefined
					? undefined
					: (event: MouseEvent<HTMLButtonElement>) => {
							// Typed as returning void, so a promise can only be seen
							// by looking; a caller's `() => save()` is exactly that.
							const result: unknown = onClick(event);
							if (result instanceof Promise) {
								setPending(true);
								const done = () => setPending(false);
								// Settled either way. A rejection stays the caller's
								// to handle inside its own function, as it always was.
								result.then(done, done);
							}
						}
			}
			{...rest}
		>
			{busy ? (
				<Spinner size={iconSize} />
			) : (
				icon && <Icon as={icon} size={iconSize} mirror={mirrorIcons} className="shrink-0" />
			)}
			{children}
			{iconEnd && !busy && (
				<Icon as={iconEnd} size={iconSize} mirror={mirrorIcons} className="shrink-0" />
			)}
		</button>
	);
}

/**
 * The busy indicator: a ring with one quarter cut away, spinning.
 *
 * Drawn in CSS rather than as an icon so it inherits `currentColor` and works
 * on every variant without a second asset. `motion-reduce` stops the animation
 * and leaves the ring — somebody who has asked for less motion still needs to
 * know the button is working.
 */
function Spinner({ size }: { size: number }) {
	return (
		<span
			aria-hidden="true"
			className="inline-block shrink-0 animate-[km-spin_0.8s_linear_infinite] rounded-full border-2 border-current border-t-transparent motion-reduce:animate-none"
			style={{ width: size, height: size }}
		/>
	);
}

export interface IconButtonProps extends Omit<ButtonProps, "children" | "icon" | "iconEnd"> {
	/** The glyph. */
	icon: LucideIcon;
	/**
	 * What the control does, for assistive technology and the tooltip.
	 *
	 * Required, not optional. An icon-only button without one is announced as
	 * "button" and nothing else — the single most common way a console becomes
	 * unusable without sight.
	 */
	label: string;
}

/** A square, icon-only button. */
export function IconButton({ icon, label, size = "md", className, ...rest }: IconButtonProps) {
	return (
		<Button size={size} aria-label={label} square className={className} icon={icon} {...rest} />
	);
}
